import type {
	LivestockManagementResponse,
	ModuleFull,
} from "@features/modules/livestockManagement/types";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";
import { prepareModulePayload, transformModuleResponse } from "./factories/moduleApiUtils";

const includedPropList = [
	"id",
	"status",
	"note",
	"livestock_category_type",
	"heads_number_start",
	"heads_number_w",
	"heads_number_wo",
	"livestock_production_type_start",
	"livestock_production_type_w",
	"livestock_production_type_wo",
	"production_start",
	"production_w",
	"production_wo",
	"prp_percentage_t2_start",
	"prp_percentage_t2_w",
	"prp_percentage_t2_wo",
	"complementary_manure_management_type_start",
	"complementary_manure_management_type_w",
	"complementary_manure_management_type_wo",
	"activity",
	"livestock_category_thread",
	"heads_number_thread",
	"livestock_production_type_thread",
	"production_thread",
	"prp_percentage_t2_thread",
	"complementary_manure_management_type_thread",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"enteric_fermentation_t2_start",
	"enteric_fermentation_t2_w",
	"enteric_fermentation_t2_wo",
	"prp_ch4_t2_start",
	"prp_ch4_t2_w",
	"prp_ch4_t2_wo",
	"prp_n2o_t2_start",
	"prp_n2o_t2_w",
	"prp_n2o_t2_wo",
	"emission_factor_ch4_t2_start",
	"emission_factor_n2o_t2_start",
	"emission_factor_ch4_t2_w",
	"emission_factor_n2o_t2_w",
	"emission_factor_ch4_t2_wo",
	"emission_factor_n2o_t2_wo",
];

// Define constants for the module
const MODULE_TYPE = "Livestock";
const TAG: TagTypes = "Livestock";
const TAG_DEFAULTS: TagTypes = "LivestockDefaults";
const ENDPOINT = FEModulesObj[FEModules.LivestockManagement].path;
const percentageProps = ["prp_percentage_t2"]; //its a t2 but displayed in t1

const transformResponse = (response: LivestockManagementResponse) => transformModuleResponse<LivestockManagementResponse, ModuleFull>({
	response,
	includedPropList,
	includedT2PropList,
	percentageProps
})
const livestocksApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicLivestocksApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

export const {
	useGetLivestockQuery,
	useGetLivestockDefaultsQuery
} = livestocksApiSlice;

export const {
	useGetLivestockQuery: usePublicGetLivestockQuery,
	useGetLivestockDefaultsQuery: usePublicGetLivestockDefaultsQuery
} = publicLivestocksApiSlice;

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
	moduleData: data.module,
	tiertwoData: data.tiertwo,
	percentageProps,
	activityId
})

const mutationApiSlice = createModuleUpdateApi<ModuleFull, LivestockManagementResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
})

export const {
	useUpdateLivestockMutation,
} = mutationApiSlice;

export const livestockEndpoints = livestocksApiSlice.endpoints;
export const livestockMutationEndpoints = mutationApiSlice.endpoints;
